import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { setAuthToken, setUser, isAuthenticated } from '../utils/auth';
import { getDeviceFingerprint } from '../utils/fingerprint';
import { getStoredAttribution } from '../utils/attribution';
import './LoginPage.css';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

function SignupPage() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Bot protection
  const [_ch, setChallenge] = useState('');
  const [_hp, setHp] = useState(''); // honeypot — should stay empty
  const _loadTime = useRef(Date.now());

  useEffect(() => {
    // Fetch challenge token when page loads
    axios.get(`${API_URL}/api/auth/reg-challenge`)
      .then(res => setChallenge(res.data.token || ''))
      .catch(() => {}); // non-blocking — graceful degradation
  }, []);

  if (isAuthenticated()) {
    navigate('/subscriptions', { replace: true });
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (fullName.trim().length < 2) {
      setError('Please enter your full name.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const referralCode = localStorage.getItem('openhosst_ref') || '';
      const _fp = await getDeviceFingerprint().catch(() => null);
      const res = await axios.post(`${API_URL}/api/auth/register`, {
        email,
        password,
        fullName: fullName.trim(),
        referralCode,
        _ch,
        _hp,
        _elapsed: Date.now() - _loadTime.current,
        _fp,
        ...getStoredAttribution(),
      });
      if (res.data.success) {
        if (referralCode) localStorage.removeItem('openhosst_ref');
        setAuthToken(res.data.token);
        setUser(res.data.user);
        navigate('/verify-email', { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <header className="login-header">
        <a href="https://openhosst.com" className="login-logo">
          <img src="/logo-dark.png" alt="OpenHosst" />
        </a>
      </header>

      <main className="login-main">
        <div className="login-card">
          <div className="login-card-accent" />
          <div className="login-card-body">
          <div className="login-card-header">
            <h1 className="login-title">Create your account</h1>
            <p className="login-subtitle">Get started with managed hosting</p>
          </div>

          {error && <div className="login-error">{error}</div>}

          <form onSubmit={handleSubmit} className="login-form">
            {/* Honeypot — hidden from real users, bots will fill it */}
            <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }} aria-hidden="true">
              <label htmlFor="_website">Website</label>
              <input
                id="_website"
                type="text"
                name="website"
                value={_hp}
                onChange={e => setHp(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            <div className="login-field">
              <label htmlFor="fullName">Full name</label>
              <input
                id="fullName"
                type="text"
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                placeholder="Jane Smith"
                autoComplete="name"
                autoFocus
                required
              />
            </div>

            <div className="login-field">
              <label htmlFor="email">Email address</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                required
              />
            </div>

            <div className="login-field">
              <label htmlFor="password">Password</label>
              <div className="login-input-wrap">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Min. 8 characters"
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  className="login-eye-btn"
                  onClick={() => setShowPassword(v => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
            </div>

            <div className="login-field">
              <label htmlFor="confirmPassword">Confirm password</label>
              <div className="login-input-wrap">
                <input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Repeat your password"
                  autoComplete="new-password"
                  required
                />
              </div>
            </div>

            <button type="submit" className="login-btn" disabled={loading}>
              {loading ? 'Creating account…' : 'Create account'}
            </button>
          </form>

            <div className="login-divider"><span>Already have an account?</span></div>
            <Link to="/login" className="login-signup-btn">Sign in instead</Link>
          </div>
        </div>
      </main>
    </div>
  );
}

export default SignupPage;
